<script setup>
/**
 * Canvas visual do agente (porte do AgentFlowCanvas do navisuite, React Flow ->
 * Vue Flow). Arrasta node da paleta, conecta, clica pra editar config. O grafo
 * é salvo em agno_agents.workflow (via aiAgents/update).
 *
 * Primeira fatia: paleta + nodes + drag/connect + inspector (Proteções/guardrails
 * completo; demais nodes com nome/subtítulo). Iterar com os outros painéis depois.
 */
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watch,
  markRaw,
  provide,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { VueFlow, useVueFlow, Panel } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import { MiniMap } from '@vue-flow/minimap';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';
import '@vue-flow/minimap/dist/style.css';
import FlowNode from './canvas/FlowNode.vue';
import GuardrailsInspector from './canvas/GuardrailsInspector.vue';
import GenericInspector from './canvas/GenericInspector.vue';
import AgentNodeInspector from './canvas/AgentNodeInspector.vue';
import PhoneInspector from './canvas/PhoneInspector.vue';
import FlexcallsCallInspector from './canvas/FlexcallsCallInspector.vue';
import FlexcallsAudioInspector from './canvas/FlexcallsAudioInspector.vue';
import FlexcallsTranscriptInspector from './canvas/FlexcallsTranscriptInspector.vue';
import HttpRequestInspector from './canvas/HttpRequestInspector.vue';
import ClassifyInspector from './canvas/ClassifyInspector.vue';
import IfElseInspector from './canvas/IfElseInspector.vue';
import ApproveInspector from './canvas/ApproveInspector.vue';
import SendInspector from './canvas/SendInspector.vue';
import EndInspector from './canvas/EndInspector.vue';
import NoteInspector from './canvas/NoteInspector.vue';
import ProspectInspector from './canvas/ProspectInspector.vue';
import EscalateInspector from './canvas/EscalateInspector.vue';
import RouteInspector from './canvas/RouteInspector.vue';
import LabelInspector from './canvas/LabelInspector.vue';
import MediaInspector from './canvas/MediaInspector.vue';
import MeetingInspector from './canvas/MeetingInspector.vue';
import LeadInspector from './canvas/LeadInspector.vue';
import BookingInspector from './canvas/BookingInspector.vue';
import ToolInspector from './canvas/ToolInspector.vue';
import AgentPlayground from 'dashboard/components-next/agents/AgentPlayground.vue';
import AgentMetricsTab from 'dashboard/components-next/agents/AgentMetricsTab.vue';
import AgentKnowledgeTab from 'dashboard/components-next/agents/AgentKnowledgeTab.vue';
import AgentInboxesPanel from 'dashboard/components-next/agents/AgentInboxesPanel.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { NODE_META, PALETTE } from './canvas/nodeMeta';
import { validateFlow } from './canvas/validateFlow';
import { FLOW_TEMPLATES } from './canvas/flowTemplates';
import { layoutPositions } from './canvas/autoLayout';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const agentId = computed(() => route.params.agentId);
const accountId = computed(() => route.params.accountId);

const nodes = ref([]);
const edges = ref([]);
const selectedId = ref(null);
// Inspector só abre no duplo-clique (clique simples apenas seleciona).
const inspectorOpen = ref(false);
const saving = ref(false);
// Estado "sujo": só habilita Salvar quando algo muda no fluxo.
const dirty = ref(false);
const markDirty = () => {
  dirty.value = true;
};

// Hub do agente: o Fluxo é a tela principal; tudo do agente mora aqui.
const agentRecord = ref(null);
// aba inicial via ?tab= (ex.: vindo do atalho de caixas -> 'launch')
const VALID_VIEWS = [
  'flow',
  'config',
  'test',
  'metrics',
  'knowledge',
  'launch',
];
const view = ref(
  VALID_VIEWS.includes(route.query.tab) ? route.query.tab : 'flow'
);
const VIEWS = [
  { key: 'flow', label: 'TAB_FLOW', icon: 'i-lucide-workflow' },
  { key: 'test', label: 'TAB_TEST', icon: 'i-lucide-play-circle' },
  { key: 'metrics', label: 'TAB_METRICS', icon: 'i-lucide-bar-chart-3' },
  { key: 'knowledge', label: 'TAB_KNOWLEDGE', icon: 'i-lucide-book-open' },
  { key: 'launch', label: 'TAB_LAUNCH', icon: 'i-lucide-rocket' },
];

// Lançamento: liga/desliga o agente (persiste na hora) + conectar caixas.
const launchActive = ref(true);
const homologationEnabled = ref(false);
const homologationNumbers = ref([]);
const homologationPhone = ref('');
const savingHomologation = ref(false);
watch(agentRecord, a => {
  if (a) {
    launchActive.value = a.active ?? true;
    homologationEnabled.value = a.homologation_enabled ?? false;
    homologationNumbers.value = [...(a.homologation_numbers || [])];
  }
});
const toggleActive = async val => {
  launchActive.value = val;
  await store.dispatch('aiAgents/update', { id: agentId.value, active: val });
};
const refreshAgent = () =>
  store.dispatch('aiAgents/show', agentId.value).then(a => {
    agentRecord.value = a;
  });

const normalizeHomologationPhone = value => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('55')) return `+${digits}`;
  if (digits.length === 10 || digits.length === 11) return `+55${digits}`;
  return `+${digits}`;
};

const addHomologationPhone = () => {
  const phone = normalizeHomologationPhone(homologationPhone.value.trim());
  const digitCount = phone.replace(/\D/g, '').length;

  if (digitCount < 10 || digitCount > 15) {
    useAlert(t('AI_AGENTS.LAUNCH.HOMOLOGATION_INVALID_PHONE'));
    return;
  }
  if (homologationNumbers.value.includes(phone)) {
    useAlert(t('AI_AGENTS.LAUNCH.HOMOLOGATION_DUPLICATE_PHONE'));
    return;
  }
  if (homologationNumbers.value.length >= 5) {
    useAlert(t('AI_AGENTS.LAUNCH.HOMOLOGATION_LIMIT'));
    return;
  }

  homologationNumbers.value.push(phone);
  homologationPhone.value = '';
};

const removeHomologationPhone = phone => {
  homologationNumbers.value = homologationNumbers.value.filter(
    number => number !== phone
  );
};

const saveHomologation = async () => {
  if (homologationEnabled.value && !homologationNumbers.value.length) {
    useAlert(t('AI_AGENTS.LAUNCH.HOMOLOGATION_EMPTY'));
    return;
  }

  savingHomologation.value = true;
  try {
    const updated = await store.dispatch('aiAgents/update', {
      id: agentId.value,
      homologation_enabled: homologationEnabled.value,
      homologation_numbers: homologationNumbers.value,
    });
    agentRecord.value = updated;
    useAlert(t('AI_AGENTS.LAUNCH.HOMOLOGATION_SAVED'));
  } catch (error) {
    useAlert(error?.message || t('AI_AGENTS.MESSAGES.UPDATE_ERROR'));
  } finally {
    savingHomologation.value = false;
  }
};

const toggleHomologation = async enabled => {
  if (
    !enabled &&
    homologationEnabled.value &&
    !window.confirm(t('AI_AGENTS.LAUNCH.HOMOLOGATION_PRODUCTION_CONFIRM'))
  ) {
    return;
  }

  homologationEnabled.value = enabled;
  if (!enabled) await saveHomologation();
};

// Paleta: coluna de ícones (sempre visível) + submenu do grupo ativo.
const activeSection = ref(null);
const selectSection = section => {
  activeSection.value = activeSection.value === section ? null : section;
};
const activeItems = computed(
  () => PALETTE.find(s => s.section === activeSection.value)?.items || []
);

const {
  onConnect,
  addEdges,
  removeEdges,
  screenToFlowCoordinate,
  onNodeClick,
  onNodeDoubleClick,
  onPaneClick,
  onEdgeDoubleClick,
  onNodeDragStop,
  fitView,
  toObject,
} = useVueFlow();

const nodeTypes = { custom: markRaw(FlowNode) };

const selectedNode = computed(() =>
  nodes.value.find(n => n.id === selectedId.value)
);

// --- Conectividade: marca nodes "soltos" (sem nenhuma ligação) p/ o FlowNode --
// Nodes tocados por alguma aresta (origem ou destino).
const connectedNodeIds = computed(() => {
  const s = new Set();
  edges.value.forEach(e => {
    if (e.source) s.add(e.source);
    if (e.target) s.add(e.target);
  });
  return s;
});
// "Solto" = node que devia estar no fluxo mas não tem aresta nenhuma. 'start'
// (entrada) e 'note' (anotação) vivem soltos por natureza.
const floatingNodeIds = computed(() => {
  const s = new Set();
  nodes.value.forEach(n => {
    const k = n.data?.kind;
    if (k === 'start' || k === 'note') return;
    if (!connectedNodeIds.value.has(n.id)) s.add(n.id);
  });
  return s;
});
provide('floatingNodeIds', floatingNodeIds);

onConnect(params => {
  // eslint-disable-next-line no-use-before-define
  addEdges([decorateEdge({ ...params, animated: true })]);
  markDirty();
});
// Clique simples seleciona (pra duplicar/copiar/apagar); duplo-clique abre o
// inspector pra editar.
onNodeClick(({ node }) => {
  selectedId.value = node.id;
  inspectorOpen.value = false;
});
onNodeDoubleClick(({ node }) => {
  selectedId.value = node.id;
  inspectorOpen.value = true;
});
onPaneClick(() => {
  selectedId.value = null;
  inspectorOpen.value = false;
});
// Duplo-clique na linha remove a conexão (além da tecla Delete/Backspace).
onEdgeDoubleClick(({ edge }) => {
  removeEdges([edge.id]);
  markDirty();
});
// Arrastar um node de lugar também conta como mudança (e entra no histórico).
onNodeDragStop(() => {
  // eslint-disable-next-line no-use-before-define
  recordHistory();
  markDirty();
});

const genId = () => `n_${Date.now().toString(36)}_${nodes.value.length}`;

// --- Auto-conexão: todo bloco novo entra no fluxo (nada de node solto) -------
// Sem saída (não pode ser origem de uma ligação).
const NO_OUTPUT_KINDS = new Set(['end']);
// Múltiplas saídas (ramos) — não auto-conecta: o usuário liga o ramo certo.
const MULTI_OUTPUT_KINDS = new Set(['classify', 'if_else', 'guardrails']);
// Vivem soltos por natureza (anotações) — não exigem conexão.
const STANDALONE_KINDS = new Set(['note']);
// Gatilhos: entram no fluxo como ORIGEM (saída -> Agente), sem entrada. A
// Prospecção é uma campanha que DISPARA o fluxo (acha empresas + 1º contato);
// a resposta do lead é atendida pelo Agente conectado.
const TRIGGER_KINDS = new Set(['prospect']);

const outgoingCount = id => edges.value.filter(e => e.source === id).length;

const isSingleOutputSource = node =>
  node &&
  !NO_OUTPUT_KINDS.has(node.data?.kind) &&
  !MULTI_OUTPUT_KINDS.has(node.data?.kind) &&
  !STANDALONE_KINDS.has(node.data?.kind);

// Ponta do fluxo: node de saída única ainda sem ligação saindo, mais à direita
// (provável fim da cadeia montada até agora).
const flowTailNode = excludeId => {
  const candidates = nodes.value.filter(
    n =>
      n.id !== excludeId && isSingleOutputSource(n) && outgoingCount(n.id) === 0
  );
  if (!candidates.length) return null;
  return candidates.reduce((a, b) => (b.position.x > a.position.x ? b : a));
};

// De qual node a nova ligação deve sair ao inserir um bloco: o selecionado (se
// puder e estiver livre), senão a ponta do fluxo, senão o agente.
const connectionSource = excludeId => {
  const sel = selectedNode.value;
  if (sel && sel.id !== excludeId) {
    if (MULTI_OUTPUT_KINDS.has(sel.data?.kind)) return null;
    if (isSingleOutputSource(sel) && outgoingCount(sel.id) === 0) return sel;
  }
  return (
    flowTailNode(excludeId) ||
    nodes.value.find(n => n.data?.kind === 'agent') ||
    null
  );
};

const isToolKind = k => (k || '').startsWith('tool_');

// Linha de ferramenta (sai da porta 'tools' do Agente): picotada e âmbar, pra
// diferenciar da linha de fluxo (sólida). Fluxo segue o padrão (animada).
const TOOL_EDGE_STYLE = {
  stroke: '#f9ab00',
  strokeWidth: 1.5,
  strokeDasharray: '6 4',
};
// Todas as edges usam smoothstep (linhas ortogonais com cantos arredondados),
// que se moldam melhor à construção do fluxo do que a curva bézier padrão.
const decorateEdge = e => {
  if (!e) return e;
  const base = { ...e, type: 'smoothstep', pathOptions: { borderRadius: 14 } };
  return e.sourceHandle === 'tools'
    ? {
        ...base,
        animated: false,
        style: { ...(e.style || {}), ...TOOL_EDGE_STYLE },
      }
    : base;
};

const linkInto = (sourceNode, targetId, sourceHandle = null) => {
  if (!sourceNode || sourceNode.id === targetId) return;
  addEdges([
    decorateEdge({
      id: `e_${sourceNode.id}_${targetId}${sourceHandle ? `_${sourceHandle}` : ''}`,
      source: sourceNode.id,
      target: targetId,
      sourceHandle,
      animated: true,
    }),
  ]);
};

const insertNode = (kind, position, { openInspector = false } = {}) => {
  const meta = NODE_META[kind] || { label: kind, color: '#9aa0a6' };
  const isTrigger = TRIGGER_KINDS.has(kind);
  const isTool = isToolKind(kind);
  // Decide origem (e porta) ANTES de inserir o node novo (senão ele vira a
  // própria ponta). Gatilho/nota não recebem entrada.
  let source = null;
  let sourceHandle = null;
  if (isTrigger || STANDALONE_KINDS.has(kind)) {
    source = null;
  } else if (isTool) {
    // Ferramenta é capacidade do Agente: sai SEMPRE da porta 'tools' do Agente.
    source = nodes.value.find(n => n.data?.kind === 'agent') || null;
    sourceHandle = 'tools';
  } else {
    source = connectionSource(null);
    // Saindo do Agente p/ um bloco de fluxo -> porta de fluxo (direita, padrão).
    sourceHandle = null;
  }
  const id = genId();
  nodes.value.push({
    id,
    type: 'custom',
    position,
    data: { kind, label: meta.label, color: meta.color },
  });
  selectedId.value = id;
  inspectorOpen.value = openInspector;
  markDirty();
  // Liga DEPOIS do nextTick pra garantir que as alças do node novo já
  // renderizaram (mesma pegadinha do F5 com edges).
  nextTick(() => {
    if (isTrigger) {
      // Gatilho dispara o fluxo: liga DELE para o Agente.
      const agent = nodes.value.find(n => n.data?.kind === 'agent');
      if (agent) linkInto({ id }, agent.id);
    } else if (source) {
      linkInto(source, id, sourceHandle);
    }
  });
  return id;
};

const onDragStart = (event, kind) => {
  event.dataTransfer.setData('application/agno-node', kind);
  event.dataTransfer.effectAllowed = 'move';
};

const onDragOver = event => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
};

const onDrop = event => {
  event.preventDefault();
  const kind = event.dataTransfer.getData('application/agno-node');
  if (!kind) return;
  const position = screenToFlowCoordinate({
    x: event.clientX,
    y: event.clientY,
  });
  insertNode(kind, position);
};

// Clicar na paleta adiciona o bloco JÁ conectado na ponta do fluxo, à direita
// da âncora (selecionado/ponta/agente). Abre o inspector pra configurar na hora.
const appendNode = kind => {
  activeSection.value = null;
  // Gatilho entra acima do Agente (ele alimenta o fluxo, não continua a cadeia).
  if (TRIGGER_KINDS.has(kind)) {
    const agent = nodes.value.find(n => n.data?.kind === 'agent');
    const position = agent
      ? { x: agent.position.x, y: agent.position.y - 170 }
      : { x: 360, y: 60 };
    insertNode(kind, position, { openInspector: true });
    return;
  }
  // Ferramentas entram numa linha abaixo do Agente (penduram na porta 'tools').
  if (isToolKind(kind)) {
    const agent = nodes.value.find(n => n.data?.kind === 'agent');
    const toolCount = nodes.value.filter(n => isToolKind(n.data?.kind)).length;
    const base = agent ? agent.position : { x: 360, y: 200 };
    const position = { x: base.x - 100 + toolCount * 200, y: base.y + 220 };
    insertNode(kind, position, { openInspector: true });
    return;
  }
  const anchor =
    selectedNode.value ||
    flowTailNode(null) ||
    nodes.value.find(n => n.data?.kind === 'agent');
  const position = anchor
    ? { x: anchor.position.x + 280, y: anchor.position.y }
    : { x: 360, y: 200 };
  insertNode(kind, position, { openInspector: true });
};

const updateSelected = patch => {
  const node = selectedNode.value;
  if (!node) return;
  node.data = { ...node.data, ...patch };
  markDirty();
  // eslint-disable-next-line no-use-before-define
  recordHistorySoon();
};

const removeSelected = () => {
  const id = selectedId.value;
  if (!id) return;
  nodes.value = nodes.value.filter(n => n.id !== id);
  edges.value = edges.value.filter(e => e.source !== id && e.target !== id);
  selectedId.value = null;
  inspectorOpen.value = false;
  markDirty();
};

// Aplica um grafo no canvas. IMPORTANTE: seta os nodes, espera o nextTick (pra
// os handles dos nodes renderizarem) e SÓ ENTÃO seta os edges — senão o Vue Flow
// descarta as ligações que apontam pra handles ainda não montados (bug do F5).
const applyGraph = async graph => {
  nodes.value = graph.nodes || [];
  edges.value = [];
  // Espera o Vue Flow registrar os nodes (state.nodes) antes de aplicar os
  // edges — senão findNode falha e o edge é descartado.
  await nextTick();
  edges.value = (Array.isArray(graph.edges) ? graph.edges : []).map(
    decorateEdge
  );
};

const loadFlow = workflow => {
  if (workflow && Array.isArray(workflow.nodes)) {
    return applyGraph(workflow);
  }
  // grafo inicial mínimo: start -> agent
  return applyGraph({
    nodes: [
      {
        id: 'start',
        type: 'custom',
        position: { x: 80, y: 200 },
        data: { kind: 'start', label: 'Início', color: '#34a853' },
      },
      {
        id: 'agent',
        type: 'custom',
        position: { x: 360, y: 200 },
        data: { kind: 'agent', label: 'Agente', color: '#4285f4' },
      },
    ],
    edges: [
      { id: 'e-start-agent', source: 'start', target: 'agent', animated: true },
    ],
  });
};

// O 1º node Agente do fluxo é a config REAL do agente.
const primaryAgentNode = list =>
  (list || nodes.value).find(n => n.data?.kind === 'agent');

// Ao abrir, popula o node com o que está salvo nos campos reais do agente,
// pra editar o de verdade (e não sobrescrever com vazio ao salvar).
const syncAgentNodeFromRecord = () => {
  const a = agentRecord.value;
  const node = primaryAgentNode();
  if (!a || !node) return;
  node.data = {
    ...node.data,
    label: a.name || node.data.label,
    persona_name: a.persona_name ?? node.data.persona_name ?? '',
    reasoningEffort: a.reasoning_effort ?? node.data.reasoningEffort ?? 'off',
    meta: a.meta ?? node.data.meta ?? null,
    businessInfo: a.business_info ?? node.data.businessInfo ?? '',
    description: a.description ?? node.data.description ?? '',
    instruction:
      a.instruction ?? a.system_prompt ?? node.data.instruction ?? '',
    model: a.model || node.data.model || 'gpt-4.1-mini',
    provider: a.provider || node.data.provider || 'openai',
    responseBackend:
      a.config?.response_backend ?? node.data.responseBackend ?? 'provider_api',
    codexModel: a.config?.codex_model ?? node.data.codexModel ?? null,
    transferMessage: a.handoff_message ?? node.data.transferMessage ?? '',
    response_delay_ms: a.response_delay_ms ?? node.data.response_delay_ms ?? 0,
    audio_silence_threshold_ms:
      a.audio_silence_threshold_ms ??
      node.data.audio_silence_threshold_ms ??
      2000,
    aggregate_messages:
      a.aggregate_messages ?? node.data.aggregate_messages ?? false,
    aggregation_window_ms:
      a.aggregation_window_ms ?? node.data.aggregation_window_ms ?? 3000,
    splitMessages: a.humanize_delivery !== false,
    humanizeLines: a.humanize_lines ?? node.data.humanizeLines ?? '',
    alwaysSearchKb: a.always_search_kb === true,
    response_mode: a.response_mode ?? node.data.response_mode,
    schedule: a.schedule ?? node.data.schedule,
    tts_enabled: a.tts_enabled ?? node.data.tts_enabled ?? false,
    tts_provider: a.tts_provider || node.data.tts_provider || 'openai',
    tts_voice: a.tts_voice || node.data.tts_voice || 'nova',
    tts_model: a.tts_model || node.data.tts_model || 'tts-1',
    tts_speed: a.tts_speed ?? node.data.tts_speed ?? 1.0,
    tts_format: a.tts_format || node.data.tts_format || 'mp3',
  };
};

const save = async () => {
  saving.value = true;
  try {
    const graph = toObject();
    const payload = {
      id: agentId.value,
      workflow: { nodes: graph.nodes, edges: graph.edges },
    };
    // O 1º node Agente é a config real: grava seus campos no agente.
    const node = primaryAgentNode(graph.nodes);
    if (node) {
      const d = node.data || {};
      const map = {
        name: d.label,
        persona_name: d.persona_name,
        reasoning_effort: d.reasoningEffort,
        meta: d.meta || { kind: '' },
        business_info: d.businessInfo,
        description: d.description,
        instruction: d.instruction,
        model: d.model,
        provider: d.provider,
        config: {
          response_backend: d.responseBackend || 'provider_api',
          codex_model: d.codexModel || null,
        },
        handoff_message: d.transferMessage,
        response_delay_ms: d.response_delay_ms,
        audio_silence_threshold_ms: d.audio_silence_threshold_ms,
        aggregate_messages: d.aggregate_messages,
        aggregation_window_ms: d.aggregation_window_ms,
        humanize_delivery: d.splitMessages,
        humanize_lines: d.humanizeLines,
        always_search_kb: d.alwaysSearchKb,
        response_mode: d.response_mode,
        schedule: d.schedule,
        tts_enabled: d.tts_enabled,
        // Sem o provider a voz Studio/clonada ficava gravada como 'openai' e a
        // síntese falhava — a IA "escolhia áudio" e respondia em texto (28/08).
        tts_provider: d.tts_provider,
        tts_voice: d.tts_voice,
        tts_model: d.tts_model,
        tts_speed: d.tts_speed,
        tts_format: d.tts_format,
      };
      Object.entries(map).forEach(([k, v]) => {
        if (v !== undefined) payload[k] = v;
      });
    }
    // O nó "Enviar resposta" grava o modo de áudio do agente (não é só prompt):
    // texto→text, áudio→always, ambas→smart (decide por mensagem no backend).
    // A matriz "Modo de Resposta" manda mais que o nó: qualquer célula pedindo
    // áudio/ambos força smart — senão a matriz vira configuração morta (o
    // backend deriva igual; aqui é só pra UI não mentir).
    const sendNode = (graph.nodes || []).find(n => n.data?.kind === 'send');
    const matrixWantsAudio = Object.values(payload.response_mode || {}).some(
      v => v === 'audio' || v === 'both'
    );
    if (matrixWantsAudio) {
      payload.tts_mode = 'smart';
    } else if (sendNode) {
      const fmt = sendNode.data?.responseFormat || 'text';
      payload.tts_mode =
        { text: 'text', audio: 'always', both: 'smart' }[fmt] || 'text';
    }
    // O nó "Telefone" é a fonte da config de voz do agente.
    const phoneNode = (graph.nodes || []).find(n => n.data?.kind === 'phone');
    const phoneDevice = phoneNode?.data?.call_device_id;
    payload.voice_enabled = !!(phoneNode && phoneDevice);
    payload.call_device_id = phoneDevice || null;
    payload.voice_mode = phoneNode?.data?.voice_mode || 'outbound';
    payload.voice_who_first = phoneNode?.data?.voice_who_first || 'caller';
    // Interrupção default ON (barge-in + AEC, experiência validada); false só se
    // o usuário desligar explicitamente no nó Telefone.
    payload.voice_allow_interrupt =
      phoneNode?.data?.voice_allow_interrupt !== false;
    payload.voice_sensitivity = phoneNode?.data?.voice_sensitivity || 'medium';
    payload.voice_response_speed =
      phoneNode?.data?.voice_response_speed || 'normal';
    payload.voice_half_duplex = phoneNode
      ? phoneNode.data?.voice_half_duplex !== false
      : true;
    payload.voice_max_minutes = Number(phoneNode?.data?.voice_max_minutes) || 5;
    payload.voice_realtime_voice =
      phoneNode?.data?.voice_realtime_voice || 'marin';
    const updated = await store.dispatch('aiAgents/update', payload);
    if (updated) agentRecord.value = updated;
    dirty.value = false;
    useAlert(t('AI_AGENTS.FLOW.SAVE_SUCCESS'));
  } catch (error) {
    // Sem isso o save falhava MUDO (ex.: app reiniciando) e o usuário achava
    // que o fluxo tinha sido salvo.
    useAlert(error?.message || t('AI_AGENTS.FLOW.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

const back = () =>
  router.push({
    name: 'ai_agents_list',
    params: { accountId: accountId.value },
  });

// ---------------------------------------------------------------------------
// Edição pro: regras de conexão, histórico (undo/redo), duplicar/copiar-colar,
// atalhos de teclado e validação do fluxo.
// ---------------------------------------------------------------------------

// Impede ligações inválidas: Início como destino, Fim como origem, auto-loop e
// aresta duplicada.
const isValidConnection = conn => {
  if (!conn.source || !conn.target || conn.source === conn.target) return false;
  const src = nodes.value.find(n => n.id === conn.source);
  const tgt = nodes.value.find(n => n.id === conn.target);
  if (!src || !tgt) return false;
  if (src.data?.kind === 'end') return false;
  if (tgt.data?.kind === 'start') return false;
  // Gatilhos (Prospecção) são origem-only: ninguém liga PARA eles.
  if (TRIGGER_KINDS.has(tgt.data?.kind)) return false;
  // Portas tipadas do Agente: porta 'tools' só aceita ferramenta; a porta de
  // fluxo (padrão) não aceita ferramenta. Mantém as saídas sem ambiguidade.
  if (src.data?.kind === 'agent') {
    const viaTools = conn.sourceHandle === 'tools';
    const targetIsTool = isToolKind(tgt.data?.kind);
    // O nó HTTP Request também é uma ferramenta do agente (vira http_tool no
    // runtime), então pode pendurar na porta de ferramentas.
    const targetIsHttp = tgt.data?.kind === 'http_request';
    if (viaTools && !targetIsTool && !targetIsHttp) return false;
    if (!viaTools && targetIsTool) return false;
  }
  // Anti-duplicata: ignora o PRÓPRIO edge (por id) — o Vue Flow roda esta
  // validação também ao CARREGAR os edges salvos; sem o `e.id !== conn.id` cada
  // edge é visto como duplicata de si mesmo e some no F5.
  return !edges.value.some(
    e =>
      e.id !== conn.id &&
      e.source === conn.source &&
      e.target === conn.target &&
      (e.sourceHandle ?? null) === (conn.sourceHandle ?? null)
  );
};

// Histórico por snapshots do grafo.
const past = ref([]);
const futureStack = ref([]);
let suppress = false;
let currentSnap = null;
let dataTimer = null;
const snapshot = () =>
  JSON.stringify({
    nodes: nodes.value.map(n => ({
      id: n.id,
      type: n.type || 'custom',
      position: { x: n.position.x, y: n.position.y },
      data: n.data,
    })),
    edges: edges.value.map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle ?? null,
      targetHandle: e.targetHandle ?? null,
      animated: e.animated ?? true,
    })),
  });
const recordHistory = () => {
  if (suppress) return;
  clearTimeout(dataTimer);
  const s = snapshot();
  if (s === currentSnap) return;
  if (currentSnap !== null) past.value.push(currentSnap);
  currentSnap = s;
  if (past.value.length > 60) past.value.shift();
  futureStack.value = [];
};
const recordHistorySoon = () => {
  clearTimeout(dataTimer);
  dataTimer = setTimeout(recordHistory, 500);
};
const restoreSnap = str => {
  suppress = true;
  const g = JSON.parse(str);
  nodes.value = g.nodes;
  edges.value = (g.edges || []).map(decorateEdge);
  currentSnap = str;
  selectedId.value = null;
  markDirty();
  nextTick(() => {
    suppress = false;
  });
};
const canUndo = computed(() => past.value.length > 0);
const canRedo = computed(() => futureStack.value.length > 0);
const undo = () => {
  if (!past.value.length) return;
  futureStack.value.push(currentSnap);
  restoreSnap(past.value.pop());
};
const redo = () => {
  if (!futureStack.value.length) return;
  past.value.push(currentSnap);
  restoreSnap(futureStack.value.pop());
};
// add/remove de node ou edge (de qualquer origem, inclusive tecla Delete) entra
// no histórico.
watch(
  () => `${nodes.value.length}:${edges.value.length}`,
  () => {
    if (suppress) return;
    recordHistory();
    markDirty();
  }
);

// Duplicar / copiar / colar node.
const clipboard = ref(null);
const cloneData = d => JSON.parse(JSON.stringify(d));
const duplicateSelected = () => {
  const n = selectedNode.value;
  if (!n || n.data.kind === 'start') return;
  const id = genId();
  nodes.value.push({
    id,
    type: 'custom',
    position: { x: n.position.x + 48, y: n.position.y + 48 },
    data: cloneData(n.data),
  });
  selectedId.value = id;
};
const copySelected = () => {
  const n = selectedNode.value;
  if (n && n.data.kind !== 'start') clipboard.value = cloneData(n.data);
};
const pasteClipboard = () => {
  if (!clipboard.value) return;
  const id = genId();
  nodes.value.push({
    id,
    type: 'custom',
    position: { x: 320, y: 160 },
    data: cloneData(clipboard.value),
  });
  selectedId.value = id;
};

// Atalhos de teclado (só no canvas; não atrapalha digitação em campos).
const onCanvasKeydown = event => {
  if (view.value !== 'flow') return;
  const tag = (event.target?.tagName || '').toLowerCase();
  if (
    tag === 'input' ||
    tag === 'textarea' ||
    event.target?.isContentEditable
  ) {
    return;
  }
  const mod = event.ctrlKey || event.metaKey;
  if (!mod) return;
  const key = event.key.toLowerCase();
  if (key === 'z') {
    event.preventDefault();
    if (event.shiftKey) redo();
    else undo();
  } else if (key === 'y') {
    event.preventDefault();
    redo();
  } else if (key === 'd') {
    event.preventDefault();
    duplicateSelected();
  } else if (key === 'c') {
    copySelected();
  } else if (key === 'v') {
    pasteClipboard();
  }
};
onMounted(() => window.addEventListener('keydown', onCanvasKeydown));
onBeforeUnmount(() => window.removeEventListener('keydown', onCanvasKeydown));

// Validação do fluxo (avisos não bloqueiam salvar).
const issues = computed(() => validateFlow(nodes.value, edges.value));
const errorCount = computed(
  () => issues.value.filter(i => i.level === 'error').length
);
const showIssues = ref(false);

// ---------------------------------------------------------------------------
// Templates, importar e exportar o fluxo (tudo reversível via undo; só persiste
// no Salvar).
// ---------------------------------------------------------------------------
const showMenu = ref(false);
const fileInput = ref(null);
const flowToast = ref('');
let toastTimer = null;
const toast = msg => {
  flowToast.value = msg;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    flowToast.value = '';
  }, 3000);
};

const exportFlow = () => {
  showMenu.value = false;
  const graph = toObject();
  const data = JSON.stringify(
    { nodes: graph.nodes, edges: graph.edges },
    null,
    2
  );
  const blob = new Blob([data], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fluxo-${agentRecord.value?.name || 'agente'}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

const triggerImport = () => {
  showMenu.value = false;
  fileInput.value?.click();
};
const onImportFile = async event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  try {
    const g = JSON.parse(await file.text());
    if (!Array.isArray(g.nodes)) throw new Error('invalid');
    await applyGraph(g);
    selectedId.value = null;
    inspectorOpen.value = false;
    markDirty();
    toast(t('AI_AGENTS.FLOW.IMPORT_OK'));
  } catch {
    toast(t('AI_AGENTS.FLOW.IMPORT_ERROR'));
  }
};

const loadTemplate = async tpl => {
  showMenu.value = false;
  await applyGraph(tpl.build());
  selectedId.value = null;
  inspectorOpen.value = false;
  markDirty();
};

// Seletor de variáveis: insere {{nome}} etc. no campo de texto focado do
// inspector (rastreia o último input/textarea focado — não precisa tocar em
// cada inspector). A IA preenche as variáveis no atendimento.
let lastField = null;
const onInspectorFocus = event => {
  const el = event.target;
  if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
    lastField = el;
  }
};
const showVars = ref(false);
const VARIABLES = [
  { token: '{{nome}}', key: 'NAME' },
  { token: '{{telefone}}', key: 'PHONE' },
  { token: '{{email}}', key: 'EMAIL' },
  { token: '{{empresa}}', key: 'COMPANY' },
];
const insertVariable = token => {
  showVars.value = false;
  const el = lastField;
  if (!el) return;
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  el.value = el.value.slice(0, start) + token + el.value.slice(end);
  // dispara o @input do campo -> o inspector emite update -> updateSelected.
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.focus();
  const pos = start + token.length;
  el.setSelectionRange?.(pos, pos);
};

// Auto-organizar: arruma os nós em camadas (esquerda -> direita) e recentraliza.
const autoLayout = () => {
  const pos = layoutPositions(nodes.value, edges.value);
  nodes.value = nodes.value.map(n =>
    pos[n.id] ? { ...n, position: pos[n.id] } : n
  );
  // eslint-disable-next-line no-use-before-define
  recordHistory();
  markDirty();
  nextTick(() => fitView({ duration: 300 }));
};

// Busca de nó: filtra por nome e centraliza/seleciona ao clicar.
const showSearch = ref(false);
const searchQuery = ref('');
const searchResults = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return [];
  return nodes.value
    .filter(n =>
      `${n.data?.label || ''} ${NODE_META[n.data?.kind]?.label || ''}`
        .toLowerCase()
        .includes(q)
    )
    .slice(0, 8);
});
const goToNode = node => {
  selectedId.value = node.id;
  inspectorOpen.value = false;
  showSearch.value = false;
  searchQuery.value = '';
  nextTick(() =>
    fitView({ nodes: [{ id: node.id }], duration: 300, padding: 2 })
  );
};

onMounted(async () => {
  const agent = await store.dispatch('aiAgents/show', agentId.value);
  agentRecord.value = agent;
  await loadFlow(agent?.workflow);
  syncAgentNodeFromRecord();
  // Carga inicial não conta como mudança nem como passo de histórico.
  dirty.value = false;
  past.value = [];
  futureStack.value = [];
  currentSnap = snapshot();
});
</script>

<template>
  <div class="flex h-full w-full flex-col bg-n-background">
    <header
      class="flex items-center justify-between gap-2 border-b border-n-weak px-4 py-2"
    >
      <button
        class="flex items-center gap-1 text-sm text-n-slate-11"
        @click="back"
      >
        <span class="i-lucide-arrow-left" /> {{ t('AI_AGENTS.FLOW.BACK') }}
      </button>

      <!-- Abas: Fluxo / Testar -->
      <div class="flex items-center gap-1 rounded-xl bg-n-alpha-1 p-1">
        <button
          v-for="v in VIEWS"
          :key="v.key"
          class="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors"
          :class="
            view === v.key
              ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="view = v.key"
        >
          <span :class="v.icon" />
          {{ t(`AI_AGENTS.FLOW.${v.label}`) }}
        </button>
      </div>

      <div class="flex items-center gap-2">
        <!-- Menu: modelos / importar / exportar -->
        <div v-if="view === 'flow'" class="relative">
          <button
            v-tooltip.bottom="t('AI_AGENTS.FLOW.MENU')"
            class="flex size-8 items-center justify-center rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-solid-2"
            @click="showMenu = !showMenu"
          >
            <span class="i-lucide-more-horizontal" />
          </button>
          <div
            v-if="showMenu"
            class="absolute right-0 top-10 z-40 w-56 rounded-xl border border-n-weak bg-n-solid-1 p-1.5 text-left shadow-xl"
          >
            <div
              class="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
            >
              {{ t('AI_AGENTS.FLOW.TEMPLATES_TITLE') }}
            </div>
            <button
              v-for="tpl in FLOW_TEMPLATES"
              :key="tpl.key"
              class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-n-slate-12 hover:bg-n-solid-2"
              @click="loadTemplate(tpl)"
            >
              <span class="i-lucide-layout-template text-n-slate-10" />
              {{ t(`AI_AGENTS.FLOW.TEMPLATES.${tpl.key}`) }}
            </button>
            <div class="my-1 h-px bg-n-weak" />
            <button
              class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-n-slate-12 hover:bg-n-solid-2"
              @click="triggerImport"
            >
              <span class="i-lucide-upload text-n-slate-10" />
              {{ t('AI_AGENTS.FLOW.IMPORT') }}
            </button>
            <button
              class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-n-slate-12 hover:bg-n-solid-2"
              @click="exportFlow"
            >
              <span class="i-lucide-download text-n-slate-10" />
              {{ t('AI_AGENTS.FLOW.EXPORT') }}
            </button>
          </div>
        </div>
        <input
          ref="fileInput"
          type="file"
          accept="application/json,.json"
          class="hidden"
          @change="onImportFile"
        />

        <!-- Validação do fluxo -->
        <div v-if="view === 'flow' && issues.length" class="relative">
          <button
            class="flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-sm font-medium"
            :class="
              errorCount
                ? 'border-n-ruby-7 text-n-ruby-9 hover:bg-n-ruby-3'
                : 'border-n-amber-7 text-n-amber-10 hover:bg-n-amber-3'
            "
            @click="showIssues = !showIssues"
          >
            <span class="i-lucide-alert-triangle" /> {{ issues.length }}
          </button>
          <div
            v-if="showIssues"
            class="absolute right-0 top-10 z-40 w-80 rounded-xl border border-n-weak bg-n-solid-1 p-3 text-left shadow-xl"
          >
            <div class="mb-2 flex items-center justify-between">
              <h4 class="text-sm font-semibold text-n-slate-12">
                {{ t('AI_AGENTS.FLOW.VALIDATION.TITLE') }}
              </h4>
              <button
                class="i-lucide-x text-n-slate-10"
                @click="showIssues = false"
              />
            </div>
            <ul class="space-y-1.5">
              <li
                v-for="(iss, i) in issues"
                :key="i"
                class="flex items-start gap-2 text-xs text-n-slate-11"
              >
                <span
                  class="mt-0.5 shrink-0"
                  :class="
                    iss.level === 'error'
                      ? 'i-lucide-x-circle text-n-ruby-9'
                      : 'i-lucide-alert-triangle text-n-amber-10'
                  "
                />
                <span>{{
                  t(`AI_AGENTS.FLOW.VALIDATION.${iss.key}`, iss.params || {})
                }}</span>
              </li>
            </ul>
          </div>
        </div>

        <button
          v-if="view === 'flow'"
          class="rounded-lg bg-n-brand px-3 py-1.5 text-sm font-medium text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="saving || !dirty"
          :title="!dirty ? t('AI_AGENTS.FLOW.NO_CHANGES') : ''"
          @click="save"
        >
          {{ saving ? t('AI_AGENTS.FLOW.SAVING') : t('AI_AGENTS.FLOW.SAVE') }}
        </button>
      </div>
    </header>

    <div v-show="view === 'flow'" class="flex min-h-0 flex-1">
      <!-- Canvas -->
      <div
        class="relative min-w-0 flex-1"
        @drop="onDrop"
        @dragover="onDragOver"
      >
        <VueFlow
          v-model:nodes="nodes"
          v-model:edges="edges"
          :node-types="nodeTypes"
          :delete-key-code="['Backspace', 'Delete']"
          :is-valid-connection="isValidConnection"
          fit-view-on-init
          class="bg-n-background"
        >
          <Background pattern-color="#94a3b8" :gap="18" />
          <Controls
            class="!shadow-none [&_.vue-flow__controls-button:hover]:!bg-n-solid-3 [&_.vue-flow__controls-button]:!border-b-n-weak [&_.vue-flow__controls-button]:!bg-n-solid-1 [&_.vue-flow__controls-button_svg]:!fill-n-slate-11"
          />
          <MiniMap
            class="!bg-n-solid-1 !border !border-n-weak"
            mask-color="rgba(15, 23, 42, 0.55)"
            node-color="#64748b"
            node-stroke-color="#64748b"
          />
          <Panel position="top-center">
            <span
              class="rounded bg-n-solid-2 px-2 py-1 text-xs text-n-slate-10"
            >
              {{ t('AI_AGENTS.FLOW.HINT') }}
            </span>
          </Panel>
        </VueFlow>

        <!-- Toolbar: desfazer / refazer / duplicar -->
        <div
          class="absolute right-3 top-3 z-20 flex items-center gap-0.5 rounded-xl border border-n-weak bg-n-solid-1 p-1 shadow-lg"
        >
          <button
            v-tooltip.bottom="t('AI_AGENTS.FLOW.UNDO')"
            class="flex size-8 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-solid-2 disabled:cursor-not-allowed disabled:opacity-40"
            :disabled="!canUndo"
            @click="undo"
          >
            <span class="i-lucide-undo-2" />
          </button>
          <button
            v-tooltip.bottom="t('AI_AGENTS.FLOW.REDO')"
            class="flex size-8 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-solid-2 disabled:cursor-not-allowed disabled:opacity-40"
            :disabled="!canRedo"
            @click="redo"
          >
            <span class="i-lucide-redo-2" />
          </button>
          <div class="mx-0.5 h-5 w-px bg-n-weak" />
          <button
            v-tooltip.bottom="t('AI_AGENTS.FLOW.DUPLICATE')"
            class="flex size-8 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-solid-2 disabled:cursor-not-allowed disabled:opacity-40"
            :disabled="!selectedNode || selectedNode.data.kind === 'start'"
            @click="duplicateSelected"
          >
            <span class="i-lucide-copy-plus" />
          </button>
          <div class="relative">
            <button
              v-tooltip.bottom="t('AI_AGENTS.FLOW.VARIABLES')"
              class="flex size-8 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-solid-2"
              @click="showVars = !showVars"
            >
              <span class="i-lucide-braces" />
            </button>
            <div
              v-if="showVars"
              class="absolute right-0 top-10 z-40 w-48 rounded-xl border border-n-weak bg-n-solid-1 p-1.5 shadow-xl"
            >
              <div class="px-2 py-1 text-[11px] text-n-slate-10">
                {{ t('AI_AGENTS.FLOW.VARIABLES_HINT') }}
              </div>
              <button
                v-for="v in VARIABLES"
                :key="v.token"
                class="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm text-n-slate-12 hover:bg-n-solid-2"
                @click="insertVariable(v.token)"
              >
                <span>{{ t(`AI_AGENTS.FLOW.VARS.${v.key}`) }}</span>
                <code class="text-xs text-n-slate-10">{{ v.token }}</code>
              </button>
            </div>
          </div>
          <div class="mx-0.5 h-5 w-px bg-n-weak" />
          <button
            v-tooltip.bottom="t('AI_AGENTS.FLOW.AUTO_LAYOUT')"
            class="flex size-8 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-solid-2"
            @click="autoLayout"
          >
            <span class="i-lucide-layout-dashboard" />
          </button>
          <div class="relative">
            <button
              v-tooltip.bottom="t('AI_AGENTS.FLOW.SEARCH')"
              class="flex size-8 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-solid-2"
              @click="showSearch = !showSearch"
            >
              <span class="i-lucide-search" />
            </button>
            <div
              v-if="showSearch"
              class="absolute right-0 top-10 z-40 w-60 rounded-xl border border-n-weak bg-n-solid-1 p-2 shadow-xl"
            >
              <input
                v-model="searchQuery"
                :placeholder="t('AI_AGENTS.FLOW.SEARCH_PH')"
                class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm"
              />
              <ul v-if="searchResults.length" class="mt-1.5 space-y-0.5">
                <li v-for="n in searchResults" :key="n.id">
                  <button
                    class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-n-slate-12 hover:bg-n-solid-2"
                    @click="goToNode(n)"
                  >
                    <span
                      :class="NODE_META[n.data?.kind]?.icon || 'i-lucide-box'"
                      class="text-n-slate-10"
                    />
                    <span class="truncate">{{
                      n.data?.label || NODE_META[n.data?.kind]?.label
                    }}</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Toast (importar/exportar) -->
        <div
          v-if="flowToast"
          class="absolute bottom-4 left-1/2 z-30 -translate-x-1/2 rounded-lg bg-n-solid-3 px-3 py-2 text-sm text-n-slate-12 shadow-lg"
        >
          {{ flowToast }}
        </div>

        <!-- Barra flutuante de ícones dos grupos (sobre o canvas) -->
        <div
          class="absolute left-3 top-3 z-20 flex flex-col items-center gap-1 rounded-2xl border border-n-weak bg-n-solid-1 p-1.5 shadow-lg"
          data-tour="canvas-paleta"
        >
          <button
            v-for="section in PALETTE"
            :key="section.section"
            v-tooltip.right="section.section"
            class="flex size-10 items-center justify-center rounded-xl border text-lg transition-colors"
            :class="
              activeSection === section.section
                ? 'border-n-brand text-n-brand'
                : 'border-transparent text-n-slate-11 hover:border-n-weak hover:bg-n-solid-2'
            "
            @click="selectSection(section.section)"
          >
            <span :class="section.icon" />
          </button>

          <!-- Submenu flutuante do grupo selecionado -->
          <div
            v-if="activeSection"
            class="absolute left-full top-0 z-30 ml-2 w-52 overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-xl"
          >
            <div
              class="flex items-center justify-between border-b border-n-weak px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
            >
              {{ activeSection }}
              <button
                class="i-lucide-x text-sm text-n-slate-10"
                @click="activeSection = null"
              />
            </div>
            <p class="px-3 pt-2 text-[11px] leading-tight text-n-slate-10">
              {{ t('AI_AGENTS.FLOW.PALETTE_HINT') }}
            </p>
            <div class="max-h-[64vh] overflow-y-auto p-1.5">
              <div
                v-for="item in activeItems"
                :key="item.kind"
                class="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-n-solid-2"
                draggable="true"
                @click="appendNode(item.kind)"
                @dragstart="onDragStart($event, item.kind)"
              >
                <span
                  class="flex size-7 shrink-0 items-center justify-center rounded-md text-white"
                  :style="{ backgroundColor: item.color }"
                >
                  <span :class="NODE_META[item.kind]?.icon || 'i-lucide-box'" />
                </span>
                <span class="truncate text-sm text-n-slate-12">
                  {{ item.label }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Inspector (abre no duplo-clique) -->
      <aside
        v-if="selectedNode && inspectorOpen"
        class="w-96 shrink-0 overflow-y-auto border-l border-n-weak"
        @focusin="onInspectorFocus"
      >
        <AgentNodeInspector
          v-if="selectedNode.data.kind === 'agent'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <PhoneInspector
          v-else-if="selectedNode.data.kind === 'phone'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <FlexcallsCallInspector
          v-else-if="selectedNode.data.kind === 'flexcalls_call'"
          :data="selectedNode.data"
          :agent-id="agentId"
          :flexcalls-config="agentRecord?.config?.flexcalls"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <FlexcallsAudioInspector
          v-else-if="selectedNode.data.kind === 'flexcalls_audio'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <FlexcallsTranscriptInspector
          v-else-if="selectedNode.data.kind === 'flexcalls_transcript'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <GuardrailsInspector
          v-else-if="selectedNode.data.kind === 'guardrails'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <HttpRequestInspector
          v-else-if="selectedNode.data.kind === 'http_request'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <ClassifyInspector
          v-else-if="selectedNode.data.kind === 'classify'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <IfElseInspector
          v-else-if="selectedNode.data.kind === 'if_else'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <ApproveInspector
          v-else-if="selectedNode.data.kind === 'approve'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <SendInspector
          v-else-if="selectedNode.data.kind === 'send'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <EndInspector
          v-else-if="selectedNode.data.kind === 'end'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <NoteInspector
          v-else-if="selectedNode.data.kind === 'note'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <ProspectInspector
          v-else-if="selectedNode.data.kind === 'prospect'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <EscalateInspector
          v-else-if="selectedNode.data.kind === 'tool_escalate'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <RouteInspector
          v-else-if="selectedNode.data.kind === 'tool_route_agent'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <LabelInspector
          v-else-if="selectedNode.data.kind === 'tool_apply_label'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <MediaInspector
          v-else-if="selectedNode.data.kind === 'tool_send_media'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <MeetingInspector
          v-else-if="selectedNode.data.kind === 'tool_schedule_meeting'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <LeadInspector
          v-else-if="selectedNode.data.kind === 'tool_capture_lead'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <BookingInspector
          v-else-if="selectedNode.data.kind === 'tool_build_booking_link'"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <ToolInspector
          v-else-if="selectedNode.data.kind.startsWith('tool_')"
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
        <GenericInspector
          v-else
          :data="selectedNode.data"
          @update="updateSelected"
          @remove="removeSelected"
          @close="inspectorOpen = false"
        />
      </aside>
    </div>

    <!-- Testar (Playground) -->
    <div v-if="view === 'test'" class="min-h-0 flex-1 p-4">
      <div class="mx-auto h-full max-w-3xl rounded-2xl border border-n-weak">
        <AgentPlayground
          v-if="agentRecord"
          :agent="agentRecord"
          :account-id="accountId"
        />
      </div>
    </div>

    <!-- Métricas -->
    <div v-if="view === 'metrics'" class="min-h-0 flex-1 overflow-y-auto p-4">
      <div class="mx-auto max-w-4xl">
        <AgentMetricsTab v-if="agentRecord" :agent-id="agentId" />
      </div>
    </div>

    <!-- Conhecimento (RAG) -->
    <div v-if="view === 'knowledge'" class="min-h-0 flex-1 overflow-y-auto p-4">
      <div class="mx-auto max-w-3xl">
        <AgentKnowledgeTab :agent-id="Number(agentId)" />
      </div>
    </div>

    <!-- Lançamento -->
    <div v-if="view === 'launch'" class="min-h-0 flex-1 overflow-y-auto p-4">
      <div class="mx-auto max-w-4xl space-y-6">
        <div class="rounded-2xl border border-n-weak bg-n-solid-2 p-6">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-base font-semibold text-n-slate-12">
                {{ t('AI_AGENTS.LAUNCH.ACTIVE_TITLE') }}
              </h3>
              <p class="mt-0.5 text-sm text-n-slate-11">
                {{ t('AI_AGENTS.LAUNCH.ACTIVE_DESCRIPTION') }}
              </p>
            </div>
            <div class="flex items-center gap-3">
              <span
                class="rounded-full px-2 py-1 text-xs font-medium"
                :class="
                  launchActive
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                {{
                  launchActive
                    ? t('AI_AGENTS.LAUNCH.STATUS_ON')
                    : t('AI_AGENTS.LAUNCH.STATUS_OFF')
                }}
              </span>
              <Switch
                :model-value="launchActive"
                @update:model-value="toggleActive"
              />
            </div>
          </div>
        </div>

        <div class="rounded-2xl border border-n-weak bg-n-solid-2 p-6">
          <div class="flex items-start justify-between gap-4">
            <div>
              <div class="flex items-center gap-2">
                <span class="i-lucide-flask-conical size-5 text-n-amber-10" />
                <h3 class="text-base font-semibold text-n-slate-12">
                  {{ t('AI_AGENTS.LAUNCH.HOMOLOGATION_TITLE') }}
                </h3>
              </div>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('AI_AGENTS.LAUNCH.HOMOLOGATION_DESCRIPTION') }}
              </p>
            </div>
            <Switch
              :model-value="homologationEnabled"
              @update:model-value="toggleHomologation"
            />
          </div>

          <div
            class="mt-4 rounded-xl border px-4 py-3 text-sm"
            :class="
              homologationEnabled
                ? 'border-n-amber-6 bg-n-amber-2 text-n-amber-11'
                : 'border-n-teal-6 bg-n-teal-2 text-n-teal-11'
            "
          >
            {{
              homologationEnabled
                ? t('AI_AGENTS.LAUNCH.HOMOLOGATION_STATUS', {
                    count: homologationNumbers.length,
                  })
                : t('AI_AGENTS.LAUNCH.PRODUCTION_STATUS')
            }}
          </div>

          <div v-if="homologationEnabled" class="mt-5 space-y-4">
            <div class="flex flex-col gap-2 sm:flex-row">
              <input
                v-model="homologationPhone"
                type="tel"
                :placeholder="
                  t('AI_AGENTS.LAUNCH.HOMOLOGATION_PHONE_PLACEHOLDER')
                "
                class="flex-1 rounded-xl border border-n-weak bg-n-solid-3 px-4 py-2.5 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand"
                :disabled="homologationNumbers.length >= 5"
                @keyup.enter="addHomologationPhone"
              />
              <Button
                :label="t('AI_AGENTS.LAUNCH.HOMOLOGATION_ADD')"
                icon="i-lucide-plus"
                variant="faded"
                :disabled="
                  !homologationPhone.trim() || homologationNumbers.length >= 5
                "
                @click="addHomologationPhone"
              />
            </div>

            <div
              class="flex items-center justify-between text-xs text-n-slate-10"
            >
              <span>{{ t('AI_AGENTS.LAUNCH.HOMOLOGATION_PHONE_HELP') }}</span>
              <span>
                {{
                  t('AI_AGENTS.LAUNCH.HOMOLOGATION_COUNT', {
                    count: homologationNumbers.length,
                  })
                }}
              </span>
            </div>

            <div v-if="homologationNumbers.length" class="space-y-2">
              <div
                v-for="phone in homologationNumbers"
                :key="phone"
                class="flex items-center justify-between rounded-xl bg-n-alpha-2 px-4 py-3"
              >
                <div class="flex items-center gap-3">
                  <span class="i-lucide-smartphone size-4 text-n-slate-10" />
                  <span class="font-mono text-sm text-n-slate-12">{{
                    phone
                  }}</span>
                </div>
                <button
                  type="button"
                  class="rounded-lg p-1.5 text-n-slate-10 hover:bg-n-alpha-3 hover:text-n-ruby-10"
                  :aria-label="t('AI_AGENTS.LAUNCH.HOMOLOGATION_REMOVE')"
                  @click="removeHomologationPhone(phone)"
                >
                  <span class="i-lucide-trash-2 size-4" />
                </button>
              </div>
            </div>
            <p v-else class="text-sm text-n-ruby-10">
              {{ t('AI_AGENTS.LAUNCH.HOMOLOGATION_EMPTY') }}
            </p>

            <div class="flex justify-end">
              <Button
                :label="t('AI_AGENTS.LAUNCH.HOMOLOGATION_SAVE')"
                icon="i-lucide-save"
                :is-loading="savingHomologation"
                @click="saveHomologation"
              />
            </div>
          </div>
        </div>

        <div class="rounded-2xl border border-n-weak bg-n-solid-2 p-6">
          <h3 class="mb-1 text-base font-semibold text-n-slate-12">
            {{ t('AI_AGENTS.LAUNCH.INBOXES_TITLE') }}
          </h3>
          <p class="mb-5 text-sm text-n-slate-11">
            {{ t('AI_AGENTS.LAUNCH.INBOXES_DESCRIPTION') }}
          </p>
          <AgentInboxesPanel
            v-if="agentRecord"
            :agent="agentRecord"
            @update="refreshAgent"
          />
        </div>
      </div>
    </div>
  </div>
</template>
